import { apiRequest } from "@/lib/api-client-v2";

export type VisualizerScene = {
  id: number;
  name: string;
  description?: string | null;
  organization_id?: number | null;
  center_lng?: number | null;
  center_lat?: number | null;
  zoom?: number | null;
};

export type VisualizerLibrary = {
  kls: { kls_id: number; name: string; icon: string }[];
  assets: { id: number; name: string; mime: string; url?: string | null }[];
};

export type VisualizerLegendEntry = {
  key: string;
  name: string;
  icon?: string | null;
  count: number;
};

export type VisualizerSignCard = {
  sign_id: number;
  template_id?: number | null;
  template_code?: string | null;
  template_name?: string | null;
  sections: {
    code: string;
    name: string;
    fields: {
      field_key: string;
      label: string;
      field_type: string;
      is_required: boolean;
      is_readonly: boolean;
      value?: unknown;
    }[];
  }[];
  values: Record<string, unknown>;
  message?: string | null;
};

/** JSON-пакет экспорта/импорта сцены (schema geopulseml.visualizer_scene_export). */
export type VisualizerSceneExportPackage = {
  schema: "geopulseml.visualizer_scene_export";
  version: 1;
  exported_at?: string | null;
  scene: {
    name: string;
    description?: string | null;
    center_lng?: number | null;
    center_lat?: number | null;
    zoom?: number | null;
  };
  assets: {
    local_id: string;
    name: string;
    mime: string;
    content_base64: string;
  }[];
  signs: Record<string, unknown>[];
};

export type VisualizerLayerGroup = {
  id: number;
  scene_id: number;
  name: string;
  display_order: number;
  is_locked: boolean;
  is_visible: boolean;
};

export type VisualizerSignFeature = GeoJSON.Feature<GeoJSON.Point | GeoJSON.LineString | GeoJSON.Polygon, {
  id: number;
  kind?: "point" | "line" | "polygon" | "text";
  source?: string;
  klsId?: number | null;
  symbolAssetId?: number | null;
  legendKey: string;
  name: string;
  iconUrl: string;
  sizePx: number;
  rotationDeg: number;
  strokeColor?: string;
  strokeWidth?: number;
  strokeDash?: "solid" | "dash" | "dot" | "dash_dot";
  fillColor?: string;
  fillOpacity?: number;
  label: string;
  objectId?: number | null;
  layerGroupId?: number | null;
  isLocked?: boolean;
  groupLocked?: boolean;
  effectivelyLocked?: boolean;
  isVisible?: boolean;
  groupVisible?: boolean;
  effectivelyVisible?: boolean;
}>;

const base = "/visualizer";

export const visualizerApi = {
  listScenes: () => apiRequest<VisualizerScene[]>(`${base}/scenes`),
  createScene: (name: string) =>
    apiRequest<VisualizerScene>(`${base}/scenes`, { method: "POST", data: { name } }),
  duplicateScene: (id: number, name?: string) =>
    apiRequest<VisualizerScene>(`${base}/scenes/${id}/duplicate`, {
      method: "POST",
      data: name != null ? { name } : {},
    }),
  updateScene: (id: number, data: Partial<VisualizerScene>) =>
    apiRequest<VisualizerScene>(`${base}/scenes/${id}`, { method: "PATCH", data }),
  deleteScene: (id: number) =>
    apiRequest<void>(`${base}/scenes/${id}`, { method: "DELETE" }),
  listLayerGroups: (sceneId: number) =>
    apiRequest<VisualizerLayerGroup[]>(`${base}/scenes/${sceneId}/layer-groups`),
  createLayerGroup: (
    sceneId: number,
    data: { name: string; display_order?: number; is_locked?: boolean; is_visible?: boolean },
  ) =>
    apiRequest<VisualizerLayerGroup>(`${base}/scenes/${sceneId}/layer-groups`, {
      method: "POST",
      data,
    }),
  updateLayerGroup: (
    sceneId: number,
    groupId: number,
    data: {
      name?: string;
      display_order?: number;
      is_locked?: boolean;
      is_visible?: boolean;
    },
  ) =>
    apiRequest<VisualizerLayerGroup>(`${base}/scenes/${sceneId}/layer-groups/${groupId}`, {
      method: "PATCH",
      data,
    }),
  deleteLayerGroup: (sceneId: number, groupId: number) =>
    apiRequest<void>(`${base}/scenes/${sceneId}/layer-groups/${groupId}`, { method: "DELETE" }),
  signs: (sceneId: number) =>
    apiRequest<GeoJSON.FeatureCollection>(`${base}/scenes/${sceneId}/signs`),
  createSign: (sceneId: number, data: Record<string, unknown>) =>
    apiRequest<VisualizerSignFeature>(`${base}/scenes/${sceneId}/signs`, { method: "POST", data }),
  updateSign: (sceneId: number, signId: number, data: Record<string, unknown>) =>
    apiRequest<VisualizerSignFeature>(`${base}/scenes/${sceneId}/signs/${signId}`, {
      method: "PATCH",
      data,
    }),
  deleteSign: (sceneId: number, signId: number) =>
    apiRequest<void>(`${base}/scenes/${sceneId}/signs/${signId}`, { method: "DELETE" }),
  mergeAreaSigns: (sceneId: number, signIds: number[]) =>
    apiRequest<VisualizerSignFeature>(`${base}/scenes/${sceneId}/signs/merge`, {
      method: "POST",
      data: { sign_ids: signIds },
    }),
  differenceAreaSigns: (sceneId: number, signIds: number[]) =>
    apiRequest<VisualizerSignFeature>(`${base}/scenes/${sceneId}/signs/difference`, {
      method: "POST",
      data: { sign_ids: signIds },
    }),
  splitSign: (sceneId: number, signId: number, coordinates: number[][]) =>
    apiRequest<GeoJSON.FeatureCollection>(`${base}/scenes/${sceneId}/signs/${signId}/split`, {
      method: "POST",
      data: { coordinates },
    }),
  legend: (sceneId: number) =>
    apiRequest<VisualizerLegendEntry[]>(`${base}/scenes/${sceneId}/legend`),
  library: () => apiRequest<VisualizerLibrary>(`${base}/symbol-library`),
  exportScene: (sceneId: number) =>
    apiRequest<VisualizerSceneExportPackage>(`${base}/scenes/${sceneId}/export`),
  importScene: (packageData: VisualizerSceneExportPackage) =>
    apiRequest<VisualizerScene>(`${base}/scenes/import`, {
      method: "POST",
      data: packageData,
    }),
  getSignCard: (sceneId: number, signId: number) =>
    apiRequest<VisualizerSignCard>(`${base}/scenes/${sceneId}/signs/${signId}/card`),
  putSignCard: (sceneId: number, signId: number, values: Record<string, unknown>) =>
    apiRequest<VisualizerSignCard>(`${base}/scenes/${sceneId}/signs/${signId}/card`, {
      method: "PUT",
      data: { values },
    }),
  uploadAsset: async (name: string, file: File) => {
    const body = new FormData();
    body.append("name", name);
    body.append("file", file);
    const token = localStorage.getItem("access_token");
    const response = await fetch("/api/v1/visualizer/symbol-assets", {
      method: "POST",
      headers: token ? { Authorization: `Bearer ${token}` } : undefined,
      body,
    });
    if (!response.ok) {
      const detail = await response.json().catch(() => ({}));
      throw new Error(
        typeof detail.detail === "string" ? detail.detail : "Не удалось загрузить знак",
      );
    }
    return response.json() as Promise<{ id: number; name: string; mime: string }>;
  },
};
